/** Presentation data projected from the shared native chat controller. */
import type { CogentView } from "@softmax/cogent-sdk";

export type PageContext = {
  /** The coworld's name, e.g. "Gods of the Arena". */
  coworld: string;
  /** The page's path, e.g. "/gods-of-the-arena/play". */
  path: string;
  /** League-scoped agent operations require this page's league. */
  leagueId?: string;
  /** Whether the cogent has a seat here. */
  seat: "here" | "away";
  /** The program it plays here, e.g. "khors:v282". */
  program: string | null;
  rank: number | null;
  round: number | null;
  /** "running", "queued" or "next"; null when unknown. */
  roundState: string | null;
  /** What the page itself is walking the viewer through, in words (e.g. "setup, step 4 of 6: Writes its first program"). */
  doing?: string | null;
};
export type AgentStatus = {
  /** `denied`: signed out, or the cogent is not the viewer's; nothing it says can be read. */
  kind: "awake" | "asleep" | "starting" | "offline" | "unknown" | "denied";
  /** In words, e.g. "Asleep — your message wakes it". */
  label: string;
  /** A Start control would bring it up. */
  canStart: boolean;
};

export type CogentMessage = {
  id: string;
  /** `cogent` speaks; `you` is the viewer; `event` is a system line. */
  from: "cogent" | "you" | "event";
  /** Markdown. */
  text: string;
  at: string | null;
  /** What the cogent showed with this line. */
  views?: CogentView[];
  /** Sent by the page, not typed: the page it was about. */
  context?: PageContext;
  /**
   * The viewer's own line, shown before Softmax has it back: on its way,
   * sent (until its echo replaces it), or not sent (`retry` sends it again).
   */
  pending?: "sending" | "sent" | "waiting" | "failed";
};

/** The id of a question about pressing something on the page, as opposed to one from the workspace. */
export const pageQuestion = "page-confirm";

export type CogentQuestion = {
  id: string;
  /** A short label, e.g. "Next step". */
  header: string | null;
  question: string;
  options: {
    label: string;
    description: string | null;
  }[];
  allowOther: boolean;
};
/** The workspace's own word on the conversation's work (working, recovered, interrupted, waiting for approval…). */
export type Recovery = {
  title: string;
  detail: string;
  /** It needs the viewer: an approval waits, or the work did not complete. */
  attention: boolean;
};
export type Outgoing = {
  text: string;
  /** Answering a question: one of its option labels, or the viewer's own words. */
  answer?: {
    questionId: string;
    choice: string | null;
    other: string | null;
  };
};
/** Display state and actions supplied by the shared native chat controller. */
export type CogentLive = {
  /** Called after nonempty assistant prose commits in a visible transcript. */
  recordAssistantCommit?: (messageId: string) => void;
  messages: CogentMessage[];
  draft: string | null;
  question: CogentQuestion | null;
  status: AgentStatus;
  doing: string | null;
  recovery: Recovery | null;
  /** What the viewer has approved the cogent keeping. */
  lessons: string[];
  forgetLesson(lesson: string): void;
  /** The history has arrived (until then an empty `messages` means "not yet"). */
  loaded: boolean;
  /**
   * Says a line: shown at once as the viewer's own (pending), replaced by
   * Softmax's echo of it, marked not sent on failure. False when it failed.
   */
  send(message: Outgoing): Promise<boolean>;
  /** Sends a line that was not sent again, under the same idempotency key. */
  retry(id: string): void;
  canSend: boolean;
  start(): Promise<void>;
};
